import { DigestDomain, type DigestDomainName } from '@signbento/contracts'
import { CryptoError } from './errors.js'
import { bytesToHex, toBufferSource } from './encoding.js'
import { canonicalizeJcs } from './jcs.js'

/**
 * Digest contract — PRD §7.4.
 * `signbento-evidence-v1`: SHA-256 over exact bytes, lowercase 64-character hex.
 * Metadata digests are domain-separated: SHA256(UTF8(domain) ‖ 0x00 ‖ JCS(value)).
 * Raw file hashes stay unprefixed SHA256(file bytes).
 */

export const SHA256_HEX_LENGTH = 64
const SHA256_HEX_PATTERN = /^[0-9a-f]{64}$/
const textEncoder = new TextEncoder()

export function isSha256Hex(value: string): boolean {
  return SHA256_HEX_PATTERN.test(value)
}

export async function sha256(input: Uint8Array | string): Promise<Uint8Array> {
  const bytes = typeof input === 'string' ? textEncoder.encode(input) : input
  return new Uint8Array(await globalThis.crypto.subtle.digest('SHA-256', toBufferSource(bytes)))
}

export async function sha256Hex(input: Uint8Array | string): Promise<string> {
  return bytesToHex(await sha256(input))
}

/** SHA256(UTF8(domain) ‖ 0x00 ‖ JCS(value)) — domain and version are part of the contract. */
export async function domainSeparatedDigest(
  domain: DigestDomainName,
  value: unknown,
): Promise<string> {
  if (typeof domain !== 'string' || domain.length === 0) {
    throw new CryptoError('CRYPTO_INVALID_INPUT', 'digest domain must be a non-empty string')
  }
  const canonical = canonicalizeJcs(value)
  const domainBytes = textEncoder.encode(domain)
  const valueBytes = textEncoder.encode(canonical)
  const payload = new Uint8Array(domainBytes.length + 1 + valueBytes.length)
  payload.set(domainBytes, 0)
  payload[domainBytes.length] = 0
  payload.set(valueBytes, domainBytes.length + 1)
  return sha256Hex(payload)
}

/**
 * Response-set ordering — PRD §7.4: exactly one accepted response per required
 * participant, sorted by recipient ID (UTF-16 code-unit order); duplicates are
 * rejected rather than silently deduped. Arrays are never sorted by JCS itself.
 */
export function orderResponseSet<T extends { readonly recipient_id: string }>(
  responses: readonly T[],
): T[] {
  const seen = new Set<string>()
  for (const response of responses) {
    if (seen.has(response.recipient_id)) {
      throw new CryptoError(
        'CRYPTO_INVALID_INPUT',
        `duplicate response for recipient ${response.recipient_id}`,
      )
    }
    seen.add(response.recipient_id)
  }
  return [...responses].sort((a, b) =>
    a.recipient_id < b.recipient_id ? -1 : a.recipient_id > b.recipient_id ? 1 : 0,
  )
}

export async function responseSetDigest(
  responses: readonly { readonly recipient_id: string }[],
): Promise<string> {
  return domainSeparatedDigest(DigestDomain.RESPONSE_SET, orderResponseSet(responses))
}
